import { useEffect } from 'react';
import { get } from 'lodash';
import { BrowserRouter, Route, Switch } from 'react-router-dom';
import {
  InteractionType,
  IPublicClientApplication,
  EventMessage,
  EventType,
} from '@azure/msal-browser';
import { MsalProvider, useIsAuthenticated, useMsal } from '@azure/msal-react';
import ProtectedApp from 'kosmos/ProtectedApp';
import ErrorPage from 'kosmos/pages/ErrorPage';
import LandingPage from 'kosmos/pages/LandingPage';
import { signInAuthProvider, getLoginRequest, getPasswordForgetRequest } from 'auth';
import Signup from './pages/smeSignup';
import ShortUrlPage from './pages/shortUrlPage';

export async function handleLogin(instance: IPublicClientApplication) {
  try {
    // https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/891a334291b1680a860fa606945283767bcf8815/lib/msal-browser/docs/errors.md#using-loginredirect-or-acquiretokenredirect
    await instance.handleRedirectPromise();
  } catch (e) {
    // cancel login/password reset is treated as Error with certain error code?, blame MS
    // ErrorCode AADB2C90091 is returned
    // Call login again will throw ServerError: access_denied: AADB2C90091: The user has cancelled entering self-asserted information.
    console.error(e);
  }
  await instance.loginRedirect(getLoginRequest());
}

function LandingHandler({ forceLogin }: { forceLogin?: boolean }) {
  const isAuthenticated = useIsAuthenticated();
  const { instance, accounts } = useMsal();

  useEffect(() => {
    const callbackId = instance.addEventCallback((message: EventMessage) => {
      if (
        message.eventType === EventType.LOGIN_FAILURE &&
        message.interactionType === InteractionType.Redirect
      ) {
        const errorMessage = get(message, ['error', 'errorMessage']) || '';
        // const errorCode = get(message, 'errorCode'); // cancel signup or signin 'user_cancelled'
        if (errorMessage.indexOf('AADB2C90118') > -1) {
          try {
            instance.loginRedirect(getPasswordForgetRequest()).then(() => {
              console.log(
                'Password has been reset successfully. \nPlease sign-in with your new password.',
              );
            });
          } catch (err) {
            console.log(err);
          }
        } else if (errorMessage.indexOf('AADB2C90091') > -1) {
          console.log('user cancelled operation');
        }
      }
    });
    if (forceLogin && !isAuthenticated) {
      handleLogin(instance);
    }

    return () => {
      if (callbackId) {
        instance.removeEventCallback(callbackId);
      }
    };
  }, [instance]);

  if (isAuthenticated) {
    return <ProtectedApp accountInfo={accounts[0]} />;
  } else {
    return <LandingPage login={() => handleLogin(instance)} />;
  }
}

export default function Router(): JSX.Element {
  const renderErrorPage = (code: number): JSX.Element => {
    return <ErrorPage code={code} />;
  };

  return (
    <MsalProvider instance={signInAuthProvider}>
      <BrowserRouter>
        <Switch>
          <Route path="/t/:shortCode" render={() => <ShortUrlPage />} />
          <Route path="/404" render={() => renderErrorPage(404)} />
          <Route path="/401" render={() => renderErrorPage(401)} />
          <Route path="/500" render={() => renderErrorPage(500)} />
          <Route path="/smeSignup" render={() => <Signup />} />
          <Route
            path="/organisations/:orgId/smeSignup"
            render={({
              match: {
                params: { orgId },
              },
            }) => {
              return <Signup organisationId={orgId} />;
            }}
          />
          <Route path="/" exact={true} component={LandingHandler} />
          <Route path="/home" exact={true} component={LandingHandler} />
          <Route path="*" render={() => <LandingHandler forceLogin={true} />} />
        </Switch>
      </BrowserRouter>
    </MsalProvider>
  );
}
